/**
 * Permission model ฝั่ง frontend
 *
 * ⚠️ ทั้งไฟล์นี้เป็น **UX layer เท่านั้น** — ใช้ซ่อน/ปิดปุ่มให้ผู้ใช้ไม่กดสิ่งที่ทำไม่ได้
 * ห้ามถือเป็นด่านความปลอดภัย ทุก request ยังต้องผ่าน `get_current_user_plant_scope`
 * + `require_permission` ที่ backend เสมอ (references/authorization-rbac.md ข้อ 5,
 * docs/architecture.md Dependency Rule 3)
 */

/** Action มาตรฐาน 4 ระดับต่อ module (references/authorization-rbac.md ข้อ 3) */
export const PERMISSION_ACTIONS = ['CREATE', 'VIEW', 'EDIT', 'APPROVE'] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/** System role พิเศษที่ไม่ผูกกับแผนก */
export type SystemRoleCode = 'ADMIN' | 'AUDITOR';

export interface ModulePermission {
  readonly module_code: string;
  readonly can_create: boolean;
  readonly can_view: boolean;
  readonly can_edit: boolean;
  readonly can_approve: boolean;
}

export interface PermissionSet {
  readonly employee_id: string;
  readonly display_name: string | null;
  readonly department_name: string | null;
  /** plant ที่ employee เข้าถึงได้ (`employee_plant_access`) — `['ALL']` สำหรับ Auditor */
  readonly plant_scope: readonly string[];
  readonly system_roles: readonly string[];
  readonly modules: readonly ModulePermission[];
}

/**
 * ผลลัพธ์ของการขอสิทธิ์ — แยกสถานะความล้มเหลวออกจากกันอย่างชัดเจน
 * ทุกสถานะที่ไม่ใช่ `ready` ต้องถือว่า "ไม่มีสิทธิ์" (deny-safe)
 */
export type PermissionsResult =
  | { readonly status: 'ready'; readonly permissions: PermissionSet }
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type PermissionsState = { readonly status: 'loading' } | PermissionsResult;

export type PermissionsFailureStatus = Exclude<PermissionsState['status'], 'ready' | 'loading'>;

/**
 * Module ที่จัดอยู่ในกลุ่ม System Administration
 * (ชุดเดียวกับ `SYSTEM_ADMIN_MODULE_CODES` ใน
 * `apps/api/src/cap_erp_api/modules/rbac/permissions.py`)
 *
 * ⚠️ ใช้สำหรับ **จัดกลุ่ม/ติดป้ายกำกับเท่านั้น** ห้ามใช้ตัดสินสิทธิ์
 * กติกา ADMIN/AUDITOR ถูกบังคับที่ backend แล้ว และผลลัพธ์มาถึง frontend
 * ในรูปของ `modules` ที่ผ่าน guard แล้ว (`docs/API_CONTRACT.md` —
 * "`modules` คือผลลัพธ์สุดท้ายหลังบังคับ role guard แล้ว")
 * ถ้า frontend เอาชุดนี้ไป derive กติกาซ้ำ จะเกิดความจริงชุดที่สองที่ขัดกับ
 * เซิร์ฟเวอร์ได้ ซึ่งเป็นสิ่งที่ contract เขียนประโยคนั้นไว้เพื่อป้องกัน
 */
export const SYSTEM_ADMIN_MODULE_CODES: ReadonlySet<string> = new Set([
  'RBAC_ADMIN',
  'SYSTEM_CONFIG',
  'DEVICE_REGISTRY',
]);

export function isSystemAdminModule(moduleCode: string): boolean {
  return SYSTEM_ADMIN_MODULE_CODES.has(moduleCode);
}

/**
 * ค่าที่ `plant_scope` ใช้แทน "ข้ามขอบเขตโรงงาน" สำหรับ AUDITOR
 * (`docs/API_CONTRACT.md`; ตรงกับ `CROSS_PLANT_SCOPE_MARKER` ฝั่ง backend)
 *
 * `plant_scope` ปกติเก็บ `plant.plant_code` ค่านี้จึงเป็น marker ไม่ใช่รหัสโรงงาน
 * และห้ามแสดงบนหน้าจอแบบดิบ ๆ เพราะผู้ใช้จะอ่านว่าเป็นโรงงานชื่อ "ALL"
 */
export const CROSS_PLANT_SCOPE_MARKER = 'ALL';
